import { SortDirection } from "types";
import {
	DatagridBackendFilterType,
	type BackendAdapter,
	type BackendFilter,
	type FilterValues,
	type SortConfig,
	pushBooleanFiltersIfSet,
	pushDateRangeFilters,
} from "../types";
import { DISCOVER_GAMES_CLUB_TEAM_SEARCH_FILTER_FIELDS, DiscoverGamesFilterField } from "./constants";
import { shouldIncludeDiscoverGamesSearchField } from "./nameSearchFilter";
import type { GameRow } from "./types";

/**
 * Backend adapter for DiscoverGames table.
 * Transforms filter values and sort config into the format expected by the API.
 */
export const discoverGamesBackendAdapter: BackendAdapter<GameRow> = {
	/**
	 * Transform filter values into BackendFilter[] format.
	 * Only includes filters that have a value.
	 */
	buildFilters: (filterValues: FilterValues): BackendFilter[] => {
		const filters: BackendFilter[] = [];

		if (filterValues[DiscoverGamesFilterField.Id]) {
			filters.push({
				field: DiscoverGamesFilterField.Id,
				type: DatagridBackendFilterType.Search,
				value: filterValues[DiscoverGamesFilterField.Id],
			});
		}

		for (const field of DISCOVER_GAMES_CLUB_TEAM_SEARCH_FILTER_FIELDS) {
			if (shouldIncludeDiscoverGamesSearchField(field, filterValues)) {
				filters.push({ field, type: DatagridBackendFilterType.Search, value: filterValues[field] });
			}
		}

		// Exact match filters
		if (filterValues[DiscoverGamesFilterField.AgeCategory]) {
			filters.push({
				field: DiscoverGamesFilterField.AgeCategory,
				type: DatagridBackendFilterType.Exact,
				value: filterValues[DiscoverGamesFilterField.AgeCategory],
			});
		}

		// Provider filter: backend handles ANY (provider IS NOT NULL), NONE (provider IS NULL), and exact values
		if (filterValues[DiscoverGamesFilterField.ProviderTag]) {
			filters.push({
				field: DiscoverGamesFilterField.ProviderTag,
				type: DatagridBackendFilterType.Exact,
				value: filterValues[DiscoverGamesFilterField.ProviderTag],
			});
		}

		if (filterValues[DiscoverGamesFilterField.GameAnalysisStatus]) {
			filters.push({
				field: DiscoverGamesFilterField.GameAnalysisStatus,
				type: DatagridBackendFilterType.Exact,
				value: filterValues[DiscoverGamesFilterField.GameAnalysisStatus],
			});
		}

		const priorityValue = filterValues[DiscoverGamesFilterField.Priority];
		if (Array.isArray(priorityValue) && priorityValue.length > 0) {
			const priorities = priorityValue
				.map((value) => (typeof value === "number" ? value : Number(value)))
				.filter((value) => !Number.isNaN(value));
			if (priorities.length > 0) {
				filters.push({
					field: DiscoverGamesFilterField.Priority,
					type: DatagridBackendFilterType.Inarray,
					value: priorities,
				});
			}
		}

		const gameStatusValue = filterValues[DiscoverGamesFilterField.GameStatus];
		if (Array.isArray(gameStatusValue) && gameStatusValue.length > 0) {
			filters.push({
				field: DiscoverGamesFilterField.GameStatus,
				type: DatagridBackendFilterType.Inarray,
				value: gameStatusValue.map((value) => String(value)),
			});
		}

		if (filterValues[DiscoverGamesFilterField.Owner]) {
			filters.push({
				field: DiscoverGamesFilterField.Owner,
				type: DatagridBackendFilterType.Exact,
				value: filterValues[DiscoverGamesFilterField.Owner],
			});
		}

		const countryValue = filterValues[DiscoverGamesFilterField.Country];
		if (Array.isArray(countryValue) && countryValue.length > 0) {
			filters.push({
				field: DiscoverGamesFilterField.Country,
				type: DatagridBackendFilterType.Inarray,
				value: countryValue,
			});
		}

		const clubManagerValue = filterValues[DiscoverGamesFilterField.ClubManager];
		if (Array.isArray(clubManagerValue) && clubManagerValue.length > 0) {
			filters.push({
				field: DiscoverGamesFilterField.ClubManager,
				type: DatagridBackendFilterType.Inarray,
				value: clubManagerValue,
			});
		}

		const contentManagerValue = filterValues[DiscoverGamesFilterField.ContentManager];
		if (Array.isArray(contentManagerValue) && contentManagerValue.length > 0) {
			filters.push({
				field: DiscoverGamesFilterField.ContentManager,
				type: DatagridBackendFilterType.Inarray,
				value: contentManagerValue,
			});
		}

		const assigneeValue = filterValues[DiscoverGamesFilterField.Assignee];
		if (Array.isArray(assigneeValue) && assigneeValue.length > 0) {
			filters.push({
				field: DiscoverGamesFilterField.Assignee,
				type: DatagridBackendFilterType.Inarray,
				value: assigneeValue,
			});
		}

		// Boolean filters
		pushBooleanFiltersIfSet(filters, filterValues, [
			DiscoverGamesFilterField.HasVideo,
			DiscoverGamesFilterField.IsDiscover,
			DiscoverGamesFilterField.IsVip,
			DiscoverGamesFilterField.IsHidden,
			DiscoverGamesFilterField.IsHiddenForScout,
		]);

		if (filterValues[DiscoverGamesFilterField.RequestedProcessingLevel]) {
			filters.push({
				field: DiscoverGamesFilterField.RequestedProcessingLevel,
				type: DatagridBackendFilterType.Exact,
				value: filterValues[DiscoverGamesFilterField.RequestedProcessingLevel],
			});
		}

		// Date range filters (support partial ranges - only start, only end, or both)
		pushDateRangeFilters(filters, filterValues, [
			DiscoverGamesFilterField.Date,
			DiscoverGamesFilterField.CreatedAt,
			DiscoverGamesFilterField.AnalysisRequestedAt,
		]);

		// Special filters for team/club IDs (used when viewing from a specific context)
		if (filterValues[DiscoverGamesFilterField.AnyTeamId]) {
			filters.push({
				field: DiscoverGamesFilterField.AnyTeamId,
				type: DatagridBackendFilterType.Exact,
				value: filterValues[DiscoverGamesFilterField.AnyTeamId],
			});
		}

		if (filterValues[DiscoverGamesFilterField.AnyClubId]) {
			filters.push({
				field: DiscoverGamesFilterField.AnyClubId,
				type: DatagridBackendFilterType.Exact,
				value: filterValues[DiscoverGamesFilterField.AnyClubId],
			});
		}

		return filters;
	},

	buildSort: (sort: SortConfig | undefined): { field: string; direction: string } | undefined => {
		if (sort === undefined) {
			return undefined;
		}
		const d = sort.direction.toUpperCase();
		return {
			field: sort.field,
			direction: d === SortDirection.DESC ? SortDirection.DESC : SortDirection.ASC,
		};
	},
};
